<script lang="ts">
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import BenefitCinema from '@tilelist/components/benefit-cinema/BenefitCinema.svelte';
    import BrandTile from '@tilelist/components/brand-tile/BrandTile.svelte';
    import LotteryCommunication from '@tilelist/components/lottery-communication/LotteryCommunication.svelte';
    import ProductComparisonBanner from '@tilelist/components/product-comparison/ProductComparisonBanner.svelte';
    import ProductTileItem from '@tilelist/components/product-tile-item/ProductTileItem.svelte';
    import SdaHoarding from '@tilelist/components/sda-hoarding/SdaHoarding.svelte';
    import SimilarProductHeader from '@tilelist/components/tile-items/SimilarProductHeader.svelte';
    import Slider from '@tilelist/components/tile-items/Slider.svelte';
    import {
        type ProductItem,
        type TileItem,
        type TileListItem,
        isProductItem,
    } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageContext } from '@tilelist/domain/composition/TileListPage.ts';
    import {
        type FeatureIndexOffset,
        computeFeatureIndices,
    } from '@tilelist/domain/external-data-attributes/ExternalDataAttributes.ts';
    import { shouldShowInstallments } from '@tilelist/domain/feature-visibility/InstallmentVisibility';
    import { shouldShowMultipleSizes } from '@tilelist/domain/feature-visibility/TopInfoVisibility';

    type Props = {
        tileListItems: TileListItem[];
        context: TileListPageContext;
        showTopInfos?: boolean;
        cacheRestored?: boolean;
        onRedirectToPdp?: (item: TileItem) => void;
        onProductItemUpdated?: (item: ProductItem) => void;
        featureIndexOffset: FeatureIndexOffset;
    };

    let {
        tileListItems: externalTileList,
        context,
        showTopInfos = false,
        cacheRestored = true,
        onRedirectToPdp = () => {},
        onProductItemUpdated = () => {},
        featureIndexOffset,
    }: Props = $props();

    const wiring = injectWiring(['controller']);
    const controller = $derived(wiring.controller);

    const onex = wiring.onex;
    const showSdaHoarding = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_SDA_HOARDING'));
    const showBenefitCinema = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_BENEFIT_CINEMA'));

    const tileListItems = $derived.by(() => {
        const _ = $state(externalTileList);
        return _;
    });

    /**
 *
 *
 *
 *
 *
 */
    const listItemKey = (item: TileListItem, i: number) => {
        if (!isProductItem(item)) return `${item.type}.${i}`;
        return `${item.type}.${item.localListPosition}.${item.product.id}`;
    };

    const columns = $derived(context?.layout?.columns);

    const showInstallments = $derived(shouldShowInstallments(context.searchContext));
    const showMultipleSizes = $derived(shouldShowMultipleSizes(context.searchContext));
    const showRadioEquipmentRegulation = $derived(
        onex.isToggleActive('ENABLE_FEATURE_FUAG_COMPONENT_ON_TILE') ?? false,
    );

    const featureIndices = $derived(
        computeFeatureIndices(tileListItems, featureIndexOffset, showBenefitCinema, showSdaHoarding),
    );
</script>

<section class={['reptile-tile-list', { [`reptile-tile-list__column--${columns}`]: columns }]}>
    {#each tileListItems as item, i (listItemKey(item, i))}
        {#if isProductItem(item)}
            <ProductTileItem
                {cacheRestored}
                productItem={item}
                rule={context.rule}
                layout={context.layout}
                loadVariation={async (variationId) => {
                    const updated = await controller.loadVariation(item, variationId);
                    if (!updated) return;
                    tileListItems[i] = updated;
                    onProductItemUpdated(updated);
                }}
                loadColorVariation={async (payload) => {
                    const updated = await controller.loadColorVariation(item, payload);
                    if (!updated) return;
                    tileListItems[i] = updated;
                    onProductItemUpdated(updated);
                }}
                onSeen={async () => {
                    controller.trackVisibility(item);
                }}
                onTileClick={(event) => {
                    controller.trackClick(event, item, context.rule);
                    onRedirectToPdp(item);
                }}
                {showRadioEquipmentRegulation}
                {showTopInfos}
                {showInstallments}
                {showMultipleSizes}
            />
        {:else if item.type === 'SPH'}
            <SimilarProductHeader />
        {:else if item.type === 'BrandTile' && showSdaHoarding}
            <BrandTile
                {cacheRestored}
                campaign={item.campaign}
                localListPosition={item.localListPosition}
                actualListPosition={item.actualListPosition}
                featureIndex={featureIndices.get(i)?.featureIndex}
                rule={context.rule}
                onSeen={async () => {
                    controller.trackVisibility(item);
                }}
                onTileClick={() => {
                    controller.trackBrandTileClick(item.actualListPosition, context.rule);
                    onRedirectToPdp(item);
                }}
            />
        {:else if item.type === 'SLIDER'}
            <Slider {...item} />
        {:else if item.type === 'PCB'}
            <ProductComparisonBanner />
        {:else if item.type === 'SponsoredDisplayAd' && showSdaHoarding}
            <SdaHoarding
                listSlot="list-slot"
                searchContext={item.searchContext}
                featureIndex={featureIndices.get(i)?.featureIndex}
                hidden={item.hidden}
            />
        {:else if item.type === 'BenefitCinema' && showBenefitCinema}
            <BenefitCinema
                dreson={context.rule}
                position="list"
                tilelistSlotIndex={featureIndices.get(i)?.tilelistSlotIndex}
                featureIndex={featureIndices.get(i)?.featureIndex}
            />
        {:else if item.type === 'LotteryCommunication'}
            <LotteryCommunication />
        {/if}
    {/each}
</section>

<!-- svelte-ignore css_unused_selector -->
<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins';

    .reptile-tile-list {
        position: relative;

        display: grid;
        grid-auto-rows: minmax(0, min-content);
        gap: tokens.$oc-semantic-spacing-25;

        margin: tokens.$oc-semantic-spacing-25;

        @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
            gap: tokens.$oc-semantic-spacing-50;
            margin: tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-50 tokens.$oc-semantic-spacing-50
                tokens.$oc-semantic-spacing-25;
        }

        &__column {
            &--one-two {
                grid-template-columns: minmax(0, 1fr);

                @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
            }

            &--two-three {
                /*                                                               */
                grid-template-columns: repeat(2, minmax(0, 1fr));

                @include mixins.breakpoint-from-l($to: false, $containerFirst: false) {
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                }
            }
        }
    }
</style>
